import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  AgentCreate,
  AgentDetail,
  AgentExternalConfig,
  AgentExternalConfigPut,
  AgentKnowledgeBaseItem,
  AgentKnowledgeBasesPut,
  AgentMcpItem,
  AgentMcpsPut,
  AgentOttoConfig,
  AgentOttoConfigPut,
  AgentPreviewPromptIn,
  AgentPreviewPromptOut,
  AgentRatingPut,
  AgentRatingsResponse,
  AgentReportTheme,
  AgentReportThemePut,
  AgentSkill,
  AgentSkillsPut,
  AgentStatus,
  AgentUpdate,
  AgentVisibilityData,
  AgentVisibilityPut,
  ExternalConnectionTest,
} from "@/api/types";

const KEY = ["agents"] as const;
// Listas de /me (Meus agentes) — invalidadas/atualizadas junto com ["agents"].
const ME_CREATED = ["me", "created-agents"] as const;
const ME_INSTALLED = ["me", "installed-agents"] as const;
const KEY_ITEM = (agentId: string) => ["agent", agentId] as const;

// Snapshot de caches de listas de agentes para rollback otimista.
type AgentListSnapshot = [readonly unknown[], AgentDetail[] | undefined][];

function snapshotAgentLists(qc: ReturnType<typeof useQueryClient>): AgentListSnapshot {
  return [
    ...qc.getQueriesData<AgentDetail[]>({ queryKey: KEY }),
    ...qc.getQueriesData<AgentDetail[]>({ queryKey: ME_CREATED }),
  ];
}

function restoreAgentLists(
  qc: ReturnType<typeof useQueryClient>,
  snapshot: AgentListSnapshot,
) {
  snapshot.forEach(([key, data]) => qc.setQueryData(key, data));
}

function invalidateAgentLists(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: KEY });
  void qc.invalidateQueries({ queryKey: ME_CREATED });
  void qc.invalidateQueries({ queryKey: ME_INSTALLED });
}
const KEY_KBS = (agentId: string) => ["agent-kbs", agentId] as const;
const KEY_MCPS = (agentId: string) => ["agent-mcps", agentId] as const;
const KEY_SKILLS = (agentId: string) => ["agent-skills", agentId] as const;
const KEY_VIS = (agentId: string) => ["agent-visibility", agentId] as const;
const KEY_OTTO = (agentId: string) => ["agent-otto-config", agentId] as const;
const KEY_EXTERNAL = (agentId: string) =>
  ["agent-external-config", agentId] as const;
const KEY_REPORT_THEME = (agentId: string) =>
  ["agent-report-theme", agentId] as const;
const KEY_RATINGS = (agentId: string) => ["agent-ratings", agentId] as const;

interface AgentsFilters {
  include_archived?: boolean;
  owner_team_id?: string;
  status?: AgentStatus;
}

export function useAgents(filters: AgentsFilters = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters.include_archived !== undefined) {
        params.include_archived = filters.include_archived;
      }
      if (filters.owner_team_id) {
        params.owner_team_id = filters.owner_team_id;
      }
      if (filters.status) params.status = filters.status;
      return api.GET<AgentDetail[]>("/agents", { params });
    },
  });
}

export function useAgent(agentId: string) {
  return useQuery({
    queryKey: KEY_ITEM(agentId),
    queryFn: () => api.GET<AgentDetail>(`/agents/${agentId}`),
    enabled: Boolean(agentId),
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentCreate) =>
      api.POST<AgentDetail>("/agents", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateAgent(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AgentUpdate) =>
      api.PATCH<AgentDetail>(`/agents/${agentId}`, patch),
    // Otimista: aplica o patch (ex.: status archived/draft na inativação/
    // reativação) nas listas em cache; reverte no erro; reconcilia ao fim.
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: KEY });
      await qc.cancelQueries({ queryKey: ME_CREATED });
      const snapshot = snapshotAgentLists(qc);
      const apply = (list?: AgentDetail[]) =>
        list?.map((a) => (a.id === agentId ? { ...a, ...patch } : a));
      qc.setQueriesData<AgentDetail[]>({ queryKey: KEY }, apply);
      qc.setQueriesData<AgentDetail[]>({ queryKey: ME_CREATED }, apply);
      return { snapshot };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.snapshot) restoreAgentLists(qc, ctx.snapshot);
    },
    onSettled: () => {
      invalidateAgentLists(qc);
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
    },
  });
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) =>
      api.DELETE<void>(`/agents/${agentId}`),
    onMutate: async (agentId) => {
      await qc.cancelQueries({ queryKey: KEY });
      await qc.cancelQueries({ queryKey: ME_CREATED });
      const snapshot = snapshotAgentLists(qc);
      const remove = (list?: AgentDetail[]) =>
        list?.filter((a) => a.id !== agentId);
      qc.setQueriesData<AgentDetail[]>({ queryKey: KEY }, remove);
      qc.setQueriesData<AgentDetail[]>({ queryKey: ME_CREATED }, remove);
      return { snapshot };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.snapshot) restoreAgentLists(qc, ctx.snapshot);
    },
    onSettled: () => {
      invalidateAgentLists(qc);
    },
  });
}

export function useDuplicateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) =>
      api.POST<AgentDetail>(`/agents/${agentId}/duplicate`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// --- Associations: KBs -----------------------------------------------------

export function useAgentKbs(agentId: string) {
  return useQuery({
    queryKey: KEY_KBS(agentId),
    queryFn: () =>
      api.GET<AgentKnowledgeBaseItem[]>(`/agents/${agentId}/knowledge-bases`),
    enabled: Boolean(agentId),
  });
}

export function useSetAgentKbs(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentKnowledgeBasesPut) =>
      api.PUT<AgentKnowledgeBaseItem[]>(
        `/agents/${agentId}/knowledge-bases`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_KBS(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// --- Associations: MCPs ----------------------------------------------------

export function useAgentMcps(agentId: string) {
  return useQuery({
    queryKey: KEY_MCPS(agentId),
    queryFn: () => api.GET<AgentMcpItem[]>(`/agents/${agentId}/mcps`),
    enabled: Boolean(agentId),
  });
}

export function useSetAgentMcps(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentMcpsPut) =>
      api.PUT<AgentMcpItem[]>(`/agents/${agentId}/mcps`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_MCPS(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// --- Associations: Skills --------------------------------------------------

export function useAgentSkills(agentId: string) {
  return useQuery({
    queryKey: KEY_SKILLS(agentId),
    queryFn: () => api.GET<AgentSkill[]>(`/agents/${agentId}/skills`),
    enabled: Boolean(agentId),
  });
}

export function useSetAgentSkills(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentSkillsPut) =>
      api.PUT<AgentSkill[]>(`/agents/${agentId}/skills`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_SKILLS(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// --- Visibility ------------------------------------------------------------

export function useAgentVisibility(agentId: string) {
  return useQuery({
    queryKey: KEY_VIS(agentId),
    queryFn: () =>
      api.GET<AgentVisibilityData>(`/agents/${agentId}/visibility`),
    enabled: Boolean(agentId),
  });
}

export function useSetAgentVisibility(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentVisibilityPut) =>
      api.PUT<AgentVisibilityData>(`/agents/${agentId}/visibility`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_VIS(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// --- Otto config -----------------------------------------------------------

export function useAgentOttoConfig(agentId: string) {
  return useQuery({
    queryKey: KEY_OTTO(agentId),
    queryFn: () =>
      api.GET<AgentOttoConfig | null>(`/agents/${agentId}/otto-config`),
    enabled: Boolean(agentId),
  });
}

export function useUpsertAgentOttoConfig(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentOttoConfigPut) =>
      api.PUT<AgentOttoConfig>(`/agents/${agentId}/otto-config`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_OTTO(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
    },
  });
}

// --- Conexao externa (agente proxy de bot HTTP remoto) ---------------------

export function useAgentExternalConfig(agentId: string) {
  return useQuery({
    queryKey: KEY_EXTERNAL(agentId),
    queryFn: () =>
      api.GET<AgentExternalConfig | null>(
        `/agents/${agentId}/external-config`,
      ),
    enabled: Boolean(agentId),
  });
}

export function useUpsertAgentExternalConfig(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentExternalConfigPut) =>
      api.PUT<AgentExternalConfig>(
        `/agents/${agentId}/external-config`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_EXTERNAL(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
    },
  });
}

export function useTestExternalConnection(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.POST<ExternalConnectionTest>(
        `/agents/${agentId}/external-config/test-connection`,
        {},
      ),
    onSuccess: () => {
      // Atualiza health_status/last_health_check_at exibidos na aba.
      void qc.invalidateQueries({ queryKey: KEY_EXTERNAL(agentId) });
    },
  });
}

// --- Report theme (identidade visual dos relatorios) ----------------------

export function useAgentReportTheme(agentId: string) {
  return useQuery({
    queryKey: KEY_REPORT_THEME(agentId),
    queryFn: () =>
      api.GET<AgentReportTheme | null>(`/agents/${agentId}/report-theme`),
    enabled: Boolean(agentId),
  });
}

export function useUpsertAgentReportTheme(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentReportThemePut) =>
      api.PUT<AgentReportTheme>(`/agents/${agentId}/report-theme`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_REPORT_THEME(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
    },
  });
}

// --- Ratings ---------------------------------------------------------------

export function useAgentRatings(agentId: string) {
  return useQuery({
    queryKey: KEY_RATINGS(agentId),
    queryFn: () =>
      api.GET<AgentRatingsResponse>(`/agents/${agentId}/ratings`),
    enabled: Boolean(agentId),
  });
}

export function usePutAgentRating(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentRatingPut) =>
      api.PUT(`/agents/${agentId}/rating`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_RATINGS(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
    },
  });
}

export function useDeleteAgentRating(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.DELETE<void>(`/agents/${agentId}/rating`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_RATINGS(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
    },
  });
}

// --- Prompt history (Langfuse-backed) --------------------------------------

export interface PromptVersion {
  version: number | null;
  content: string;
  labels: string[];
  commit_message: string | null;
  created_at: string | null;
}

const KEY_PROMPT_HISTORY = (agentId: string) =>
  ["agent-prompt-history", agentId] as const;

export function useAgentPromptHistory(agentId: string) {
  return useQuery({
    queryKey: KEY_PROMPT_HISTORY(agentId),
    queryFn: () =>
      api.GET<PromptVersion[]>(`/agents/${agentId}/prompt-history`),
    enabled: Boolean(agentId),
  });
}

export function useRollbackAgentPrompt(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { version: number; commit_message?: string }) =>
      api.POST(`/agents/${agentId}/prompt/rollback`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_ITEM(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_PROMPT_HISTORY(agentId) });
    },
  });
}

// --- Preview prompt --------------------------------------------------------

export function useAgentPreviewPrompt() {
  return useMutation({
    mutationFn: (params: { agentId: string; overrides: AgentPreviewPromptIn }) =>
      api.POST<AgentPreviewPromptOut>(
        `/agents/${params.agentId}/preview-prompt`,
        params.overrides,
      ),
  });
}
